import React, { useEffect, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Switch,
  Input,
  InputField,
  ScrollView,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useFormContext, Controller, useWatch } from 'react-hook-form';
import {
  useUpdateMcqQuestion,
  useDeleteMcqQuestion,
} from '@/common/api/endpoints/mcq/mcq';
import { getGetTasksByModuleVersionQueryKey } from '@/common/api/endpoints/tasks/tasks';
import type { MCQQuestion } from '@/common/api/models';
import { useDialog } from '@/common/context/DialogContext';
import ContentGroup from '@/common/components/ContentGroup';
import { QuestionList } from './QuestionList';
import { QuestionEditorModal } from './QuestionEditorModal';
import type { TaskFormData } from '../utils/taskEditorSchemas';

/**
 * Section header component following Flare design hierarchy
 */
const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text size="xs" fontWeight="bold" color="$textSubtle" textTransform="uppercase" letterSpacing="$md" mb="$2">
    {children}
  </Text>
);

interface MCQConfigFormProps {
  moduleId: string;
  version: string;
  taskId: string;
  questions?: MCQQuestion[];
  isReadOnly?: boolean;
}

export const MCQConfigForm: React.FC<MCQConfigFormProps> = ({
  moduleId,
  version,
  taskId,
  questions = [],
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<TaskFormData>();
  // Store only the ID - derive the question from props (query cache) each render
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
  const selectedQuestion = questions.find((q) => q.id === selectedQuestionId) ?? null;

  const { showToastMessage } = useDialog();
  const queryClient = useQueryClient();
  const updateQuestion = useUpdateMcqQuestion();
  const deleteQuestion = useDeleteMcqQuestion();
  const eventKindId = useWatch({ control, name: 'eventConfig.eventKindId' });
  const isProctoredMode = eventKindId === 'proctored_exam';

  useEffect(() => {
    if (isProctoredMode) {
      setValue('mcqConfig.showImmediateFeedback', false, { shouldDirty: false });
      setValue('eventConfig.unlockConditions.type', 'all_previous', { shouldDirty: false });
      setValue('eventConfig.unlockConditions.taskIds', [], { shouldDirty: false });
    }
  }, [isProctoredMode, setValue]);

  const invalidateTaskQueries = () => {
    queryClient.invalidateQueries({ queryKey: getGetTasksByModuleVersionQueryKey(moduleId, version) });
  };

  const handleQuestionSave = (updatedQuestion: MCQQuestion) => {
    updateQuestion.mutate({
      moduleId,
      version,
      taskId,
      questionId: updatedQuestion.id,
      data: {
        text: updatedQuestion.text,
        explanation: updatedQuestion.explanation,
        difficulty: updatedQuestion.difficulty,
        options: updatedQuestion.options,
      },
    }, {
      onSuccess: () => {
        showToastMessage('success', t('lecturer.moduleEditor.taskEditor.mcq.messages.questionSaved'));
        setSelectedQuestionId(null);
        invalidateTaskQueries();
      },
      onError: () => {
        showToastMessage('error', t('lecturer.moduleEditor.taskEditor.mcq.messages.questionFailed'));
      },
    });
  };

  const handleQuestionDelete = (questionId: string) => {
    deleteQuestion.mutate({
      moduleId,
      version,
      taskId,
      questionId,
    }, {
      onSuccess: () => {
        showToastMessage('success', t('lecturer.moduleEditor.taskEditor.mcq.messages.questionDeleted'));
        setSelectedQuestionId(null);
        invalidateTaskQueries();
      },
      onError: () => {
        showToastMessage('error', t('lecturer.moduleEditor.taskEditor.mcq.messages.questionDeleteFailed'));
      },
    });
  };

  return (
    <Box flex={1} bg="$bgApp">
      <ScrollView flex={1} bg="$bgApp">
        <VStack space="md" px="$4" py="$3">
          {/* MCQ Settings */}
          <VStack space="sm">
            <Text size="sm" fontWeight="medium" color="$textSubtle" letterSpacing={0.3}>
              {t('lecturer.moduleEditor.taskEditor.mcq.settingsTitle')}
            </Text>
            <ContentGroup variant="bordered" p="$3" accentWidth={4}>
              <VStack space="md">
                {/* Pass Threshold */}
                <HStack alignItems="center" space="md">
                  <Text size="xs" color="$textSubtle" flexShrink={0}>
                    {t('lecturer.moduleEditor.taskEditor.mcq.passThreshold')}
                  </Text>
                  <Controller
                    control={control}
                    name="mcqConfig.passThresholdPercent"
                    render={({ field: { value, onChange } }) => (
                      <HStack flex={1} alignItems="center" space="md">
                        <Slider
                          flex={1}
                          value={(value ?? 70) / 100}
                          onChange={(val) => onChange(Math.round(val * 100))}
                          minValue={0}
                          maxValue={1}
                          step={0.05}
                          isDisabled={isReadOnly}
                          testID="pass-threshold-slider"
                        >
                          <SliderTrack>
                            <SliderFilledTrack />
                          </SliderTrack>
                          <SliderThumb />
                        </Slider>
                        <Text size="xs" color="$textSubtle" minWidth={36}>
                          {Math.round(value ?? 70)}
                          %
                        </Text>
                      </HStack>
                    )}
                  />
                </HStack>

                {/* Options and Time Limit in one flowing row */}
                <HStack flexWrap="wrap" space="xl" alignItems="center">
                  {/* Limit Questions Shown */}
                  <HStack alignItems="center" space="sm">
                    <Controller
                      control={control}
                      name="mcqConfig.questionsToShow"
                      render={({ field: { value, onChange } }) => (
                        <Switch
                          size="sm"
                          value={value !== null && value !== undefined}
                          onValueChange={(enabled) => onChange(enabled ? questions.length || 1 : null)}
                          isDisabled={isReadOnly}
                          testID="limit-questions-toggle"
                        />
                      )}
                    />
                    <Text size="xs">
                      {t('lecturer.moduleEditor.taskEditor.mcq.sampleQuestions')}
                    </Text>
                  </HStack>

                  <Controller
                    control={control}
                    name="mcqConfig.questionsToShow"
                    render={({ field: { value, onChange } }) => (
                      value !== null && value !== undefined ? (
                        <HStack alignItems="center" space="sm">
                          <Text size="xs" color="$textSubtle">
                            {t('lecturer.moduleEditor.taskEditor.mcq.sampleQuestionsCount')}
                          </Text>
                          <Input isDisabled={isReadOnly} size="sm" w={80}>
                            <InputField
                              value={value?.toString() || ''}
                              onChangeText={(val) => {
                                const num = parseInt(val, 10);
                                onChange(Number.isNaN(num) ? 1 : num);
                              }}
                              keyboardType="numeric"
                              size="sm"
                              editable={!isReadOnly}
                              testID="limit-questions-input"
                            />
                          </Input>
                        </HStack>
                      ) : <Box />
                    )}
                  />

                  {/* Shuffle Questions */}
                  <HStack alignItems="center" space="sm">
                    <Controller
                      control={control}
                      name="mcqConfig.shuffleQuestions"
                      render={({ field: { value, onChange } }) => (
                        <Switch
                          size="sm"
                          value={value ?? true}
                          onValueChange={onChange}
                          isDisabled={isReadOnly}
                          testID="shuffle-questions-toggle"
                        />
                      )}
                    />
                    <Text size="xs">
                      {t('lecturer.moduleEditor.taskEditor.mcq.shuffleQuestions')}
                    </Text>
                  </HStack>

                  {/* Shuffle Options */}
                  <HStack alignItems="center" space="sm">
                    <Controller
                      control={control}
                      name="mcqConfig.shuffleOptions"
                      render={({ field: { value, onChange } }) => (
                        <Switch
                          size="sm"
                          value={value ?? true}
                          onValueChange={onChange}
                          isDisabled={isReadOnly}
                          testID="shuffle-options-toggle"
                        />
                      )}
                    />
                    <Text size="xs">
                      {t('lecturer.moduleEditor.taskEditor.mcq.shuffleOptions')}
                    </Text>
                  </HStack>

                  {/* Show Feedback */}
                  <HStack alignItems="center" space="sm">
                    <Controller
                      control={control}
                      name="mcqConfig.showImmediateFeedback"
                      render={({ field: { value, onChange } }) => (
                        <Switch
                          size="sm"
                          value={value ?? true}
                          onValueChange={onChange}
                          isDisabled={isReadOnly}
                          testID="immediate-feedback-toggle"
                        />
                      )}
                    />
                    <Text size="xs">
                      {t('lecturer.moduleEditor.taskEditor.mcq.showFeedback')}
                    </Text>
                  </HStack>

                  {/* Time Limit */}
                  <HStack alignItems="center" space="sm">
                    <Text size="xs" color="$textSubtle">
                      {t('lecturer.moduleEditor.taskEditor.mcq.timeLimit')}
                    </Text>
                    <Controller
                      control={control}
                      name="mcqConfig.timeLimitMinutes"
                      render={({ field: { value, onChange } }) => (
                        <Input isDisabled={isReadOnly} size="sm" w={80}>
                          <InputField
                            value={value?.toString() || ''}
                            onChangeText={(val) => {
                              const num = parseInt(val, 10);
                              onChange(Number.isNaN(num) ? null : num);
                            }}
                            placeholder={t('lecturer.moduleEditor.taskEditor.mcq.timeLimitPlaceholder')}
                            keyboardType="numeric"
                            size="sm"
                            editable={!isReadOnly}
                            testID="time-limit-input"
                          />
                        </Input>
                      )}
                    />
                  </HStack>
                </HStack>
              </VStack>
            </ContentGroup>
          </VStack>

          {/* Proctored Delivery Mode */}
          <VStack space="sm">
            <Text size="sm" fontWeight="medium" color="$textSubtle" letterSpacing={0.3}>
              {t('lecturer.moduleEditor.taskEditor.mcq.proctoredMode')}
            </Text>
            <ContentGroup variant="bordered" p="$3" accentWidth={4}>
              <VStack space="sm">
                <Text size="xs" color="$textSubtle">
                  {t('lecturer.moduleEditor.taskEditor.mcq.proctoredDescription')}
                </Text>
                <Controller
                  control={control}
                  name="eventConfig.eventKindId"
                  render={({ field: { value, onChange } }) => (
                    <HStack space="sm" alignItems="center">
                      <Switch
                        size="sm"
                        value={value === 'proctored_exam'}
                        onValueChange={(enabled) => onChange(enabled ? 'proctored_exam' : undefined)}
                        isDisabled={isReadOnly}
                        testID="enable-proctoring-toggle"
                      />
                      <Text size="xs">
                        {t('lecturer.moduleEditor.taskEditor.mcq.enableProctoring')}
                      </Text>
                    </HStack>
                  )}
                />
                {!!isProctoredMode && (
                  <Text size="xs" color="$textSubtle">
                    {t('lecturer.moduleEditor.taskEditor.mcq.proctoredUnlockPolicy')}
                  </Text>
                )}
              </VStack>
            </ContentGroup>
          </VStack>

          {/* Question Management Section */}
          <Box>
            <SectionHeader>{t('lecturer.moduleEditor.taskEditor.mcq.questionsTitle')}</SectionHeader>
            <QuestionList
              moduleId={moduleId}
              version={version}
              taskId={taskId}
              questions={questions}
              onQuestionClick={setSelectedQuestionId}
              isReadOnly={isReadOnly}
            />
          </Box>
        </VStack>

        {/* Question Editor Modal */}
        {!!selectedQuestion && (
          <QuestionEditorModal
            isOpen
            onClose={() => setSelectedQuestionId(null)}
            moduleId={moduleId}
            version={version}
            taskId={taskId}
            question={selectedQuestion}
            onSave={handleQuestionSave}
            onDelete={handleQuestionDelete}
            isReadOnly={isReadOnly}
          />
        )}
      </ScrollView>
    </Box>
  );
};

export default MCQConfigForm;
